"use client";

import Image from "next/image";
import type { VehicleT } from "@/app/types/gebrauchtwagen";
import { formatPrice } from "@/app/utils/helpers/format";
import Link from "next/link";

import { cn } from "@/lib/utils";
import { CheckIcon, MapPinIcon } from "lucide-react";
import { generateSlug } from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen-old/lib/vehicles-formatter";
import { renderEnvkvConsumption } from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen-old/lib/vehicles-formatter";
import { Badge } from "@/app/components/ui/badge";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";
import { buildBrandPath } from "@/app/config/brand-registry";

import type { VehicleWithLabelsT } from "@/app/types/api";

interface VehicleCardCompactPropsT {
  vehicle: VehicleT | VehicleWithLabelsT;
  /**
   * Default = full info (availability, location, etc.)
   * offer-like = reduced info (title, consumption, price) to mirror offer cards.
   */
  variant?: "default" | "offer-like";
}

// Type for old image format
type OldImageFormat = { m?: string; s?: string };

// Helper function to get first image URL (supports both old and new format)
function firstImage(
  images: string[] | OldImageFormat[] | null | undefined
): string {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return "/nothing-found.webp";
  }

  // New format: string[]
  if (typeof images[1] === "string") {
    return images[1];
  }

  // Old format: object with size properties
  const firstImageObj = images[1];
  if (
    firstImageObj &&
    typeof firstImageObj === "object" &&
    !Array.isArray(firstImageObj)
  ) {
    const img = firstImageObj as OldImageFormat;
    return img.m || img.s || "/nothing-found.webp";
  }

  return "/nothing-found.webp";
}

// Helper function to get other images (supports both old and new format)
function getOtherImages(
  images: string[] | OldImageFormat[] | null | undefined
): string[] {
  if (!images || !Array.isArray(images)) {
    return [];
  }

  return images
    .slice(2, 5)
    .map((img) => {
      // New format: string[]
      if (typeof img === "string") {
        return img;
      }
      // Old format: object with size properties
      if (img && typeof img === "object" && !Array.isArray(img)) {
        const imageObj = img as OldImageFormat;
        return imageObj.m || imageObj.s || null;
      }
      return null;
    })
    .filter((src): src is string => typeof src === "string" && src.length > 0);
}

/**
 * Compact vehicle card component for grid layouts.
 * Displays vehicle information in a compact, vertical layout optimized for smaller spaces.
 */
export default function VehicleCardCompact({
  vehicle,
  variant = "default",
}: VehicleCardCompactPropsT) {
  const { brandKey, isSkoda, isAudi } = useBrand();
  const firstImageUrl = firstImage(vehicle.images);
  const otherImageSrcs = getOtherImages(vehicle.images);

  // Get make and model directly from vehicle (support both formats)
  const make =
    ("makeLabel" in vehicle && vehicle.makeLabel) || vehicle.make || "";
  const model =
    ("modelLabel" in vehicle && vehicle.modelLabel) || vehicle.model || "";
  const vehicleShortName = `${make} ${model}`.trim();
  const vehicleSlug = vehicle.vehicleid
    ? generateSlug(vehicleShortName, vehicle.vehicleid)
    : "#";
  const vehicleHref =
    vehicleSlug === "#"
      ? "#"
      : buildBrandPath(brandKey, `/gebrauchtwagen/${vehicleSlug}`);

  const isOfferLike = variant === "offer-like";
  const isSkodaOfferLike = isSkoda && isOfferLike;
  const isAudiOfferLike = isAudi && isOfferLike;

  return (
    <Link
      href={vehicleHref}
      prefetch={false}
      className={cn(
        "block h-full w-full",
        isSkodaOfferLike && "text-black hover:text-black",
        isAudiOfferLike && "text-white hover:text-white"
      )}
    >
      <div
        className={cn(
          "bg-background relative flex h-full w-full min-w-[260px] flex-col overflow-hidden rounded-lg border text-black transition-colors hover:bg-[#e9e9e9]",
          isSkodaOfferLike &&
            "rounded-none border-0 bg-white shadow-[0_6px_16px_hsl(0_0%_20%_/_0.10)] hover:bg-white",
          isAudiOfferLike &&
            "rounded-[20px] border-0 bg-[#1D2129] text-white shadow-none hover:bg-[#252b36]"
        )}
      >
        <div className="flex w-full flex-col">
          <div className="relative w-full">
            <div className="relative aspect-[16/9] w-full">
              <div
                className={cn(
                  "absolute inset-0 grid h-full w-full gap-0.5",
                  otherImageSrcs.length > 1
                    ? "grid-cols-[3fr_1fr]"
                    : "grid-cols-1"
                )}
              >
                <div className="relative h-full w-full overflow-hidden">
                  <Image
                    fill
                    sizes="(min-width: 1280px) 380px, (min-width: 768px) 300px, 100vw"
                    src={firstImageUrl}
                    alt={vehicleShortName || "Fahrzeug"}
                    className="object-cover"
                  />
                </div>
                {otherImageSrcs.length > 1 ? (
                  <div className="grid h-full grid-rows-3 gap-0.5">
                    {otherImageSrcs.map((src, idx) => (
                      <div
                        key={idx}
                        className="relative w-full overflow-hidden"
                      >
                        <div className="relative aspect-[16/10] h-full w-full">
                          <Image
                            fill
                            sizes="(min-width: 768px) 100px, 33vw"
                            src={src}
                            alt={vehicleShortName || "Fahrzeug"}
                            className="object-cover"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div
            className={cn(
              "flex w-full flex-col",
              isOfferLike ? "p-4" : "p-4 sm:p-6 xl:p-8"
            )}
          >
            <h3
              className={cn(
                isOfferLike ? "heading-small" : "text-xl font-semibold",
                isSkodaOfferLike && "text-black",
                isAudiOfferLike && "text-white"
              )}
            >
              {vehicleShortName}
            </h3>
            <div className="flex grow flex-col gap-3 pt-1">
              {isOfferLike ? (
                <div className="mt-auto flex flex-col gap-2">
                  <div
                    className={cn(
                      "text-muted-foreground text-xs leading-relaxed",
                      isSkodaOfferLike && "text-black",
                      isAudiOfferLike && "text-[#a0a8b4]"
                    )}
                  >
                    {renderEnvkvConsumption(vehicle.envkv, "text-xs") ?? (
                      <span>Kraftstoffverbrauch: k.A.</span>
                    )}
                  </div>
                  <div
                    className={cn(
                      "flex flex-row items-end gap-1 pt-2 text-base font-semibold",
                      isSkodaOfferLike && "text-black",
                      isAudiOfferLike && "text-white"
                    )}
                  >
                    {vehicle.customerprice
                      ? formatPrice(vehicle.customerprice)
                      : "Preis auf Anfrage"}
                    {vehicle.customerprice && (
                      <span
                        className={cn(
                          "mb-1 text-xs font-normal",
                          isSkodaOfferLike && "text-black",
                          isAudiOfferLike && "text-[#a0a8b4]"
                        )}
                      >
                        {" "}
                        inkl.{" "}
                        {vehicle.sellflags16
                          ? "MwSt. (ausweisbar)"
                          : "MwSt. (nicht ausweisbar)"}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-auto flex flex-col gap-2 pt-2">
                  <div className="flex flex-row items-end gap-1 text-base font-semibold">
                    {vehicle.customerprice
                      ? formatPrice(vehicle.customerprice)
                      : "Preis auf Anfrage"}
                    {vehicle.customerprice && (
                      <span className="mb-1 text-xs font-normal">
                        {" "}
                        inkl.{" "}
                        {vehicle.sellflags16
                          ? "MwSt. (ausweisbar)"
                          : "MwSt. (nicht ausweisbar)"}
                      </span>
                    )}
                  </div>
                  <Badge className="text-muted-foreground bg-muted w-fit text-xs">
                    <CheckIcon className="size-3" /> Sofort verfügbar
                  </Badge>
                  <div className="text-muted-foreground mt-2 flex items-start gap-2 text-xs font-normal">
                    <span className="mt-0.5 size-2 shrink-0 rounded-full">
                      <MapPinIcon size={12} />
                    </span>
                    {`${vehicle.sellingdealer?.firm || ""}, ${vehicle.sellingdealer?.street || ""},${vehicle.sellingdealer?.dealercountry || ""}-${vehicle.sellingdealer?.postalcode || ""} ${vehicle.sellingdealer?.city || ""}`}
                  </div>
                  <div className="text-muted-foreground flex w-full flex-col gap-3">
                    {renderEnvkvConsumption(vehicle.envkv, "text-xs")}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
